import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { HttpError } from '../services/api';
import { User, Organization, Role, RegisterData, RegisterSuccessResponse, WorkspaceMode } from '../types/auth';
import { syncRequestOrganizationId } from '../utils/organizationRequestContext';
import { useCAWorkspaceStore } from '../modules/ca/state/caWorkspaceStore';

interface AuthState {
  user: User | null;
  organization: Organization | null;
  organizations: any[];
  roles: Role[];
  workspaceMode: WorkspaceMode;
  availableWorkspaces: WorkspaceMode[];
  isAuthenticated: boolean;
  /** True only during initial session restore — not during login/register submit */
  isBootstrapping: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthPayload = {
  user: User;
  organization: Organization | null;
  organizations: any[];
  roles: Role[];
  availableWorkspaces: WorkspaceMode[];
  workspaceMode: WorkspaceMode;
};

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: AuthPayload }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SWITCH_ORGANIZATION'; payload: { organization: Organization | null } }
  | { type: 'SET_WORKSPACE_MODE'; payload: WorkspaceMode };

const initialState: AuthState = {
  user: null,
  organization: null,
  organizations: [],
  roles: [],
  workspaceMode: 'business',
  availableWorkspaces: ['business'],
  isAuthenticated: false,
  isBootstrapping: true,
  isLoading: true,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        organization: action.payload.organization,
        organizations: action.payload.organizations || [],
        roles: action.payload.roles,
        workspaceMode: action.payload.workspaceMode,
        availableWorkspaces: action.payload.availableWorkspaces,
        isAuthenticated: true,
        isBootstrapping: false,
        isLoading: false,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        organization: null,
        organizations: [],
        roles: [],
        workspaceMode: 'business',
        availableWorkspaces: ['business'],
        isAuthenticated: false,
        isBootstrapping: false,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        organization: null,
        organizations: [],
        roles: [],
        workspaceMode: 'business',
        availableWorkspaces: ['business'],
        isAuthenticated: false,
        isBootstrapping: false,
        isLoading: false,
        error: null,
      };
    case 'SWITCH_ORGANIZATION':
      return { ...state, organization: action.payload.organization };
    case 'SET_WORKSPACE_MODE':
      return { ...state, workspaceMode: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (email: string, password: string, nextPath?: string) => Promise<string>;
  register: (data: RegisterData) => Promise<RegisterSuccessResponse>;
  verifyEmailAndLogin: (email: string, otp: string, nextPath?: string) => Promise<string>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  hasPermission: (resource: string, action: string) => boolean;
  hasRole: (roleName: string) => boolean;
  setWorkspaceMode: (mode: WorkspaceMode) => void;
  isCAWorkspace: () => boolean;
  switchOrganization: (organizationId: string) => Promise<void>;
  canSwitchOrganizations: () => boolean;
  refreshUserData: () => Promise<User>;
  getPostAuthDestination: (nextPath?: string, workspaceModeOverride?: WorkspaceMode) => string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AUTH_ACCOUNT_TYPE_HINT_KEY = 'auth_account_type_hint';
const WORKSPACE_MODE_KEY = 'workspace_mode';
const CA_ACTIVE_FIRM_ID_KEY = 'ca_active_firm_id';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

const getAvailableWorkspaces = (
  user: User | null | undefined,
  organizations: any[] = []
): WorkspaceMode[] => {
  const hasBusiness = organizations.length > 0;
  const memberships = Array.isArray(user?.ca_firm_memberships) ? user?.ca_firm_memberships ?? [] : [];
  const hasActiveCAMembership = memberships.some((membership) => membership.is_active);
  const needsCAWorkspaceSetup = user?.needs_ca_workspace_setup === true;
  const isCAAccount = user?.account_type === 'ca';
  /** CA workspace appears in the switcher when user has an active membership or still must complete CA onboarding. */
  const hasCAWorkspace =
    hasActiveCAMembership || (isCAAccount && needsCAWorkspaceSetup);

  if (hasBusiness && hasCAWorkspace) return ['business', 'ca'];
  if (hasCAWorkspace) return ['ca'];
  return ['business'];
};

const resolveWorkspaceMode = (available: WorkspaceMode[], user: User): WorkspaceMode => {
  const saved = localStorage.getItem(WORKSPACE_MODE_KEY) as WorkspaceMode | null;
  if (saved && available.includes(saved)) return saved;
  if (user.account_type === 'ca' && available.includes('ca')) return 'ca';
  return 'business';
};

const resolveActiveCAMembership = (user: User | null | undefined) => {
  if (!user) return null;
  const memberships = Array.isArray(user.ca_firm_memberships) ? user.ca_firm_memberships : [];
  return memberships.find((membership) => membership.is_active || (membership as any).active === true) ?? memberships[0] ?? null;
};

const resolveCAFirmId = (user: User | null | undefined): string => {
  if (!user) return '';
  const activeMembership = resolveActiveCAMembership(user) as any;
  const fromMembership =
    activeMembership?.ca_firm_id ||
    activeMembership?.firm_id ||
    activeMembership?.firmId ||
    activeMembership?.caFirmId ||
    '';
  if (typeof fromMembership === 'string' && fromMembership.trim()) return fromMembership.trim();

  const fromUser = (user as any)?.ca_firm_id || (user as any)?.firm_id || (user as any)?.firmId || '';
  if (typeof fromUser === 'string' && fromUser.trim()) return fromUser.trim();

  return '';
};

const resolveCAWorkspaceOrganizationId = (
  user: User | null | undefined,
  activeOrganization: Organization | null | undefined
): string | null => {
  const rawMemberships = user?.ca_firm_memberships;
  const memberships = Array.isArray(rawMemberships) ? rawMemberships : [];
  const activeMemberships = memberships.filter((m) => m.is_active || (m as any).active === true);
  const ao = user?.active_organization_id ? String(user.active_organization_id) : '';
  const preferredMembership = ao
    ? activeMemberships.find((m) => {
        const ids = [m.workspace_organization_id, m.tenant_organization_id, m.organization_id].filter(Boolean).map(String);
        return ids.includes(ao);
      })
    : undefined;

  const activeMembership =
    preferredMembership ?? resolveActiveCAMembership(user);
  const fromMembership =
    activeMembership?.workspace_organization_id ||
    activeMembership?.tenant_organization_id ||
    activeMembership?.organization_id;
  if (fromMembership) return fromMembership;
  const fromUser =
    (user as any)?.active_organization_id ||
    (user as any)?.activeOrganizationId ||
    (user as any)?.organization_id ||
    (user as any)?.organizationId ||
    (user as any)?.tenant_organization_id ||
    (user as any)?.tenantOrganizationId;
  if (fromUser) return String(fromUser);
  if (activeOrganization?.id) return activeOrganization.id;
  return null;
};

const sanitizeNextPath = (nextPath?: string): string | null => {
  if (!nextPath) return null;
  if (!nextPath.startsWith('/') || nextPath.startsWith('//')) return null;
  return nextPath;
};

const resolvePostAuthDestination = (
  workspaceMode: WorkspaceMode,
  hasBusinessWorkspace: boolean,
  nextPath: string | undefined,
  user: Pick<User, 'needs_ca_workspace_setup'> | null | undefined
): string => {
  if (workspaceMode === 'ca') {
    return user?.needs_ca_workspace_setup === true ? '/ca/workspace-setup' : '/ca/dashboard';
  }
  if (!hasBusinessWorkspace) return '/organization/setup';
  return sanitizeNextPath(nextPath) ?? '/dashboard';
};

const normalizeUser = (user: User): User => ({
  ...user,
  account_type: user.account_type === 'ca' ? 'ca' : 'business',
  ca_firm_memberships: Array.isArray(user.ca_firm_memberships) ? user.ca_firm_memberships : [],
});

export const authContextUtils = {
  getAvailableWorkspaces,
  resolveWorkspaceMode,
  resolveActiveCAMembership,
  resolveCAWorkspaceOrganizationId,
  resolvePostAuthDestination,
  normalizeUser,
};

const buildAuthPayload = (
  user: User,
  organization: Organization | null,
  organizations: any[],
  roles: Role[]
): AuthPayload => {
  const normalizedUser = normalizeUser(user);
  const availableWorkspaces = getAvailableWorkspaces(normalizedUser, organizations);
  const workspaceMode = resolveWorkspaceMode(availableWorkspaces, normalizedUser);
  const resolvedFirmId = resolveCAFirmId(normalizedUser);
  localStorage.setItem(WORKSPACE_MODE_KEY, workspaceMode);
  if (resolvedFirmId) {
    localStorage.setItem(CA_ACTIVE_FIRM_ID_KEY, resolvedFirmId);
  }

  if (normalizedUser.account_type) {
    localStorage.removeItem(AUTH_ACCOUNT_TYPE_HINT_KEY);
  }

  return {
    user: normalizedUser,
    organization,
    organizations,
    roles,
    availableWorkspaces,
    workspaceMode,
  };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      if (token && refreshToken) {
        try {
          dispatch({ type: 'AUTH_START' });
          const userData = await authService.getCurrentUser();
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: buildAuthPayload(
              userData.user,
              userData.organization ?? null,
              userData.user?.organizations || [],
              userData.roles
            ),
          });
        } catch (_error) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      } else {
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    };
    void initializeAuth();
  }, []);

  useEffect(() => {
    const handleLogout = () => dispatch({ type: 'AUTH_LOGOUT' });
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  useEffect(() => {
    const orgId =
      state.workspaceMode === 'business'
        ? state.organization?.id ?? null
        : resolveCAWorkspaceOrganizationId(state.user, state.organization);
    syncRequestOrganizationId(orgId);
  }, [state.organization, state.workspaceMode, state.user, state.user?.active_organization_id]);

  useEffect(() => {
    if (!state.isAuthenticated || !state.user) return;
    if (state.workspaceMode !== 'ca') return;
    const fromMembership = resolveCAFirmId(state.user);
    const fromStorage =
      typeof window !== 'undefined' ? localStorage.getItem(CA_ACTIVE_FIRM_ID_KEY)?.trim() || '' : '';
    const firmId = fromMembership || fromStorage;
    if (!firmId) return;
    const { activeFirmId, setFirmId } = useCAWorkspaceStore.getState();
    if (activeFirmId !== firmId) {
      setFirmId(firmId);
    }
  }, [state.isAuthenticated, state.user, state.workspaceMode]);

  useEffect(() => {
    if (!state.isAuthenticated || !state.user) return undefined;
    const refreshInterval = setInterval(async () => {
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) return;
        const response = await authService.refreshToken(refreshToken);
        localStorage.setItem('access_token', response.access_token);
        if (response.refresh_token) {
          localStorage.setItem('refresh_token', response.refresh_token);
        }
      } catch (_error) {
        if (_error instanceof HttpError && _error.status === 403) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          dispatch({ type: 'AUTH_LOGOUT' });
          window.location.assign('/login');
        }
      }
    }, 10 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [state.isAuthenticated, state.user]);

  const login = async (email: string, password: string, nextPath?: string): Promise<string> => {
    try {
      dispatch({ type: 'CLEAR_ERROR' });
      const response = await authService.login({ email, password });
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      const payload = buildAuthPayload(response.user, response.organization ?? null, response.user?.organizations || [], response.roles || []);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload,
      });
      return resolvePostAuthDestination(
        payload.workspaceMode,
        payload.availableWorkspaces.includes('business'),
        nextPath,
        payload.user
      );
    } catch (_error) {
      const errorMessage = _error instanceof Error ? _error.message : 'Login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw _error;
    }
  };

  const register = async (data: RegisterData): Promise<RegisterSuccessResponse> => {
    dispatch({ type: 'CLEAR_ERROR' });
    return authService.register(data);
  };

  const verifyEmailAndLogin = async (email: string, otp: string, nextPath?: string): Promise<string> => {
    try {
      dispatch({ type: 'CLEAR_ERROR' });
      const response = await authService.verifyEmail({ email, otp });
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      const payload = buildAuthPayload(response.user, response.organization ?? null, response.user?.organizations || [], response.roles || []);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload,
      });
      return resolvePostAuthDestination(
        payload.workspaceMode,
        payload.availableWorkspaces.includes('business'),
        nextPath,
        payload.user
      );
    } catch (_error) {
      const errorMessage = _error instanceof Error ? _error.message : 'Verification failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw _error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem(WORKSPACE_MODE_KEY);
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) throw new Error('No refresh token available');
      const response = await authService.refreshToken(refreshToken);
      localStorage.setItem('access_token', response.access_token);
      const userData = await authService.getCurrentUser();
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: buildAuthPayload(
          userData.user,
          userData.organization ?? null,
          userData.user?.organizations || [],
          userData.roles
        ),
      });
    } catch (_error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      if (_error instanceof HttpError && _error.status === 403) {
        dispatch({ type: 'AUTH_LOGOUT' });
        window.location.assign('/login');
        return;
      }
      dispatch({ type: 'AUTH_FAILURE', payload: 'Session expired' });
      throw _error;
    }
  };

  const clearError = useCallback(() => dispatch({ type: 'CLEAR_ERROR' }), []);

  const hasPermission = (resource: string, action: string): boolean => {
    if (!state.roles || !Array.isArray(state.roles)) return false;
    return state.roles.some((role) => {
      const permissions = role.permissions;
      if (!permissions) return false;
      if (permissions.all === 'all' || (permissions as any).all === true) return true;
      const resourcePermissions = permissions[resource];
      if (!resourcePermissions) return false;
      return resourcePermissions === 'all' || resourcePermissions === action;
    });
  };

  const hasRole = (roleName: string): boolean => {
    if (!state.roles || !Array.isArray(state.roles)) return false;
    return state.roles.some((role) => role.name === roleName);
  };

  const setWorkspaceMode = (mode: WorkspaceMode) => {
    if (!state.availableWorkspaces.includes(mode)) return;
    localStorage.setItem(WORKSPACE_MODE_KEY, mode);
    dispatch({ type: 'SET_WORKSPACE_MODE', payload: mode });
  };

  const isCAWorkspace = () => state.workspaceMode === 'ca';

  const canSwitchOrganizations = (): boolean => {
    if (state.workspaceMode === 'ca') return false;
    if (!state.user || !state.organizations) return false;
    return state.organizations.some((org: any) => ['owner', 'admin'].includes(org.role));
  };

  const switchOrganization = async (organizationId: string): Promise<void> => {
    await authService.switchOrganization(organizationId);
    const newOrganization = state.organizations.find((org: any) => org.organization_id === organizationId);
    dispatch({
      type: 'SWITCH_ORGANIZATION',
      payload: { organization: newOrganization?.organization || null },
    });
  };

  const refreshUserData = async (): Promise<User> => {
    try {
      const userData = await authService.getCurrentUser();
      const payload = buildAuthPayload(
        userData.user,
        userData.organization ?? null,
        userData.user?.organizations || [],
        userData.roles
      );
      dispatch({
        type: 'AUTH_SUCCESS',
        payload,
      });
      return payload.user;
    } catch (error) {
      throw error instanceof Error ? error : new Error('Refresh failed');
    }
  };

  const getPostAuthDestination = (nextPath?: string, workspaceModeOverride?: WorkspaceMode): string => {
    const mode = workspaceModeOverride ?? state.workspaceMode;
    return resolvePostAuthDestination(
      mode,
      state.availableWorkspaces.includes('business'),
      nextPath,
      state.user
    );
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    verifyEmailAndLogin,
    logout,
    refreshToken,
    clearError,
    hasPermission,
    hasRole,
    setWorkspaceMode,
    isCAWorkspace,
    switchOrganization,
    canSwitchOrganizations,
    refreshUserData,
    getPostAuthDestination,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
